import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Observable, combineLatest, map, takeUntil, filter } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxProcedureTilesComponent } from '@shared/components/rx-procedure-tiles/rx-procedure-tiles.component';
import { Procedure } from '@shared/models/procedure';
import { RxIntuitiveOrderFacade } from '@modules/rx-intuitive-order/containers/rx-intuitive-order/rx-intuitive-order.facade';
import { OrderFormConfirmationService } from '@modules/order/services/order-form-confirmation.service';

@Component({
	selector: 'rx-select-procedure',
	templateUrl: './rx-select-procedure.component.html',
	styleUrls: ['./rx-select-procedure.component.scss'],
	standalone: true,
	providers: [RxIntuitiveOrderFacade, OrderFormConfirmationService],
	imports: [AsyncPipe, RxProcedureTilesComponent]
})
export class RxSelectProcedureComponent extends BaseDestroyableComponent implements OnInit {
	@Output() procedureSelected = new EventEmitter<Procedure['Id']>();

	constructor(private readonly facade: RxIntuitiveOrderFacade) {
		super();
	}

	readonly procedureTiles$ = this.facade.procedureTiles$;
	readonly selectedProcedureId$ = this.facade.selectedProcedureId$;

	defaultProcedure$: Observable<Procedure | null> = combineLatest({
		procedureTiles: this.procedureTiles$,
		defaultProcedure: this.facade.defaultProcedure$
	}).pipe(
		filter(({ defaultProcedure, procedureTiles }) => !!defaultProcedure || procedureTiles.length === 1),
		map(({ defaultProcedure, procedureTiles }) => defaultProcedure ?? procedureTiles[0] ?? null)
	);

	onProcedureSelected(id: Procedure['Id']): void {
		this.facade.handleProcedureChanged(id);
		this.procedureSelected.emit(id);
	}

	ngOnInit(): void {
		if (this.facade.procedure) {
			this.procedureSelected.emit(this.facade.procedure.Id);
		} else {
			this.defaultProcedure$
				.pipe(
					takeUntil(this.componentAlive$),
					filter((procedure): procedure is Procedure => !!procedure)
				)
				.subscribe(procedure => this.onProcedureSelected(procedure.Id));
		}
	}
}
